import type { DefaultValues, FieldValues, UseFormProps } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { z } from 'zod'

/**
 * Helper sobre `useForm` que já injeta o `zodResolver`.
 *
 *   const schema = z.object({ name: z.string().min(1, 'Obrigatório') })
 *   type Form = z.infer<typeof schema>
 *
 *   const form = useZodForm(schema, { defaultValues: { name: '' } })
 *
 * O retorno é o mesmo do `react-hook-form` (register, handleSubmit, etc).
 *
 * Vantagem: garante consistência ao longo do projeto — todos os forms
 * obrigatoriamente passam por validação zod.
 */
export function useZodForm<S extends z.ZodType<FieldValues>>(
  schema: S,
  options?: Omit<UseFormProps<z.infer<S>>, 'resolver'>,
) {
  type FormValues = z.infer<S>
  return useForm<FormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
    ...options,
    defaultValues: options?.defaultValues as DefaultValues<FormValues>,
  })
}
